import React, { useState, useEffect, useCallback } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { API_URL } from "../../config";
import Notification from "../../components/Notification";
import { formatToDayMonthYear } from "../../utils/TextUtils";
import { allowedToView } from "../../utils/PermissionUtils";

function SchoolMissionsTab() {
    const { id: schoolId } = useParams();
    const [data, setData] = useState({
        missions: [],
        current_page: 1,
        last_page: 1,
        per_page: 15,
        total: 0
    });
    const [missionTypes, setMissionTypes] = useState([]);
    const [terms, setTerms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [notification, setNotification] = useState({ type: "", message: "" });

    const [filters, setFilters] = useState({
        term_id: "",
        mission_type_id: "",
        page: 1,
        per_page: 15
    });

    const userRole = localStorage.getItem("userRole");
    const canEdit = (userRole) => allowedToView(userRole, ['admin', 'superadmin', 'missions-coordinator']);
    const isAdmin = (userRole) => allowedToView(userRole, ['admin', 'superadmin', 'missions-coordinator', 'treasurer']);
    const navigate = useNavigate();
    const location = useLocation();
    
    // Fetch mission types and terms once on mount
    useEffect(() => {
        const fetchMetaData = async () => {
            try {
                const token = localStorage.getItem("access_token");

                const [typesRes, termsRes] = await Promise.all([
                    fetch(`${API_URL}/mission-types`, {
                        headers: { "Authorization": `Bearer ${token}` }
                    }),
                    fetch(`${API_URL}/terms`, {
                        headers: { "Authorization": `Bearer ${token}` }
                    })
                ]);

                if (typesRes.ok && termsRes.ok) {
                    const typesData = await typesRes.json();
                    const termsData = await termsRes.json();

                    setMissionTypes(typesData.data || typesData);
                    setTerms(termsData.data || termsData);
                } else {
                    throw new Error("Failed to load metadata");
                }
            } catch (error) {
                console.error("Metadata Fetch Error:", error);
            }
        };

        fetchMetaData();
    }, []);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("access_token");
            const queryParams = new URLSearchParams();
            
            // Always filter by school ID
            queryParams.append("school_id", schoolId);
            
            // Add other filters
            Object.entries(filters).forEach(([key, value]) => {
                if (value) queryParams.append(key, value);
            });

            const response = await fetch(`${API_URL}/missions?${queryParams}`, {
                headers: { "Authorization": `Bearer ${token}` }
            });

            if (response.ok) {
                const missionsData = await response.json();
                setData({
                    missions: missionsData.data,
                    current_page: missionsData.current_page,
                    last_page: missionsData.last_page,
                    per_page: missionsData.per_page,
                    total: missionsData.total
                });
            } else if (response.status === 401) {
                navigate("/logout", { state: { from: location } });
            } else {
                throw new Error("Failed to fetch missions");
            }
        } catch (error) {
            setError("An error occurred while fetching data.");
            console.error("Fetch Data Error:", error);
        } finally {
            setLoading(false);
        }
    }, [filters, schoolId, navigate, location]);

    useEffect(() => {
        fetchData();
    }, [filters.term_id, filters.mission_type_id, filters.page, filters.per_page, fetchData]);

    const isPastMission = (startDate) => {
        if (!startDate) return false;
        const today = new Date();
        const missionDate = new Date(startDate);
        return missionDate <= today;
    };

    const closeNotification = () => {
        setNotification({ type: "", message: "" });
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value,
            page: 1 // Reset to first page when filters change
        }));
    };

    const handlePageChange = (page) => {
        setFilters(prev => ({ ...prev, page }));
    };

    const handlePerPageChange = (e) => {
        const perPage = parseInt(e.target.value);
        setFilters(prev => ({
            ...prev,
            per_page: perPage,
            page: 1
        }));
    };

    return (
        <div className="school-missions-tab">
            {notification.message && (
                <Notification
                    type={notification.type}
                    message={notification.message}
                    onClose={closeNotification}
                />
            )}

            <div className="columns">
                <div className="column">
                    <div className="field">
                        <label className="label">Term</label>
                        <div className="control select is-fullwidth">
                            <select name="term_id" value={filters.term_id} onChange={handleFilterChange}>
                                <option value="">All Terms</option>
                                {terms.map(term => (
                                    <option key={term.id} value={term.id}>{term.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>
                <div className="column">
                    <div className="field">
                        <label className="label">Mission Type</label>
                        <div className="control select is-fullwidth">
                            <select name="mission_type_id" value={filters.mission_type_id} onChange={handleFilterChange}>
                                <option value="">All Types</option>
                                {missionTypes.map(type => (
                                    <option key={type.id} value={type.id}>{type.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>
                <div className="column">
                    <div className="field">
                        <label className="label">Items per page</label>
                        <div className="control">
                            <div className="select is-fullwidth">
                                <select value={filters.per_page} onChange={handlePerPageChange}>
                                    {[5, 10, 15, 25, 50].map(size => (
                                        <option key={size} value={size}>{size}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="has-text-centered">
                    <p>Loading missions...</p>
                    <progress className="progress is-small is-primary" max="100">15%</progress>
                </div>
            ) : error ? (
                <p className="has-text-danger">{error}</p>
            ) : (
                <>
                    <div className="table-container">
                        <table className="table is-striped is-fullwidth">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Type</th>
                                    <th>Start Date</th>
                                    <th>End Date</th>
                                    <th>Status</th>
                                    <th>Leader</th>
                                    <th>Feedback</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.missions.length > 0 ? (
                                    data.missions.map((mission, index) => (
                                        <tr key={mission.id}>
                                            <td>{(data.current_page - 1) * data.per_page + index + 1}</td>
                                            <td>
                                                <Link to={`/dashboard/missions/${mission.id}/details`} className="default-color">
                                                    {`${mission.mission_type?.name}`}
                                                </Link>
                                            </td>
                                            <td>
                                                {mission.start_date && formatToDayMonthYear(mission.start_date)}
                                                {mission.start_time && `, ${mission.start_time}`}
                                            </td>
                                            <td>
                                                {mission.end_date && (
                                                    mission.start_date === mission.end_date
                                                        ? mission.end_time && `, ${mission.end_time}`
                                                        : `${formatToDayMonthYear(mission.end_date)}${mission.end_time ? `, ${mission.end_time}` : ''}`
                                                )}
                                            </td>
                                            <td>
                                                <span className={`tag ${mission.status === 'Active' ? 'is-success' : 'is-danger'}`}>
                                                    {mission.status || "N/A"}
                                                </span>
                                            </td>
                                            <td>
                                                {mission?.mission_leader ? (
                                                    isAdmin(userRole) ? (
                                                        <Link
                                                            to={`/dashboard/members/${mission.mission_leader.id}/details`}
                                                            className="default-color"
                                                        >
                                                            {mission.mission_leader.first_name || ''} {mission.mission_leader.last_name || ''}
                                                        </Link>
                                                    ) : (
                                                        <span>
                                                            {mission.mission_leader.first_name || ''} {mission.mission_leader.last_name || ''}
                                                        </span>
                                                    )
                                                ) : (
                                                    <span className="tag is-light">N/A</span>
                                                )}
                                            </td>
                                            <td>
                                                {isPastMission(mission.start_date) ? (
                                                    mission.has_feedback ? (
                                                        <Link to={`/dashboard/missions/${mission.id}/feedback/details`}>
                                                            <span className="tag is-success">Received</span>
                                                        </Link>
                                                    ) : (
                                                        <span className="tag is-warning">Pending</span>
                                                    )
                                                ) : (
                                                    <span className="tag is-light">N/A</span>
                                                )}
                                            </td>
                                            <td>
                                                <Link 
                                                    to={`/dashboard/missions/${mission.id}/details`} 
                                                    className="button is-small is-white"
                                                    title="View Details"
                                                >
                                                    <span className="icon">
                                                        <i className="fas fa-eye"></i>
                                                    </span>
                                                </Link>
                                                {canEdit(userRole) && (
                                                    <Link 
                                                        to={`/dashboard/missions/${mission.id}/update`} 
                                                        className="button is-small is-white"
                                                        title="Edit"
                                                    >
                                                        <span className="icon">
                                                            <i className="fas fa-edit"></i>
                                                        </span>
                                                    </Link>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="8" className="has-text-centered">No missions found for this school</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {data.total > 0 && (
                        <nav className="pagination is-centered" role="navigation" aria-label="pagination">
                            <button
                                className="pagination-previous"
                                onClick={() => handlePageChange(data.current_page - 1)}
                                disabled={data.current_page === 1}
                            >
                                Previous
                            </button>
                            <button
                                className="pagination-next"
                                onClick={() => handlePageChange(data.current_page + 1)}
                                disabled={data.current_page === data.last_page}
                            >
                                Next
                            </button>
                            <ul className="pagination-list">
                                {Array.from({ length: data.last_page }, (_, i) => i + 1).map(page => (
                                    <li key={page}>
                                        <button
                                            className={`pagination-link ${data.current_page === page ? 'is-current' : ''}`}
                                            aria-label={`Go to page ${page}`}
                                            onClick={() => handlePageChange(page)}
                                        >
                                            {page}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </nav>
                    )}
                </>
            )}
        </div>
    );
}

export default SchoolMissionsTab;
